import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import useScript from '~/client/hooks/useScript';
import isWeb from '~/client/utils/isWeb';
import { fetchFromServer } from '~/client/utils/fetchJSONFromServer';
import logger from '~/client/utils/logger';

const MUSICKIT_JS_SRC = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';

// `musickit-typescript` (stale, v1.2.3-era types) models nowPlayingItem,
// currentPlaybackTime, currentPlaybackDuration, and isPlaying as living on a
// nested `.player` object. Inspecting the real MusicKit JS v3 script
// (js-cdn.music.apple.com/musickit/v3/musickit.js) directly shows these are
// actually top-level getters on the instance itself — there is no `.player`
// at runtime, so `music.player?.x` silently evaluates to `undefined` via
// optional chaining instead of throwing. This type reflects runtime reality.
type MKInstanceRuntime = MusicKit.MusicKitInstance & {
  nowPlayingItem?: MusicKit.MediaItem;
  currentPlaybackTime?: number;
  currentPlaybackDuration?: number;
  isPlaying?: boolean;
  repeatMode?: number;
  queue?: {
    items: MusicKit.MediaItem[];
    position: number;
    removeQueueItems: (
      predicate: (
        queueItem: { item: MusicKit.MediaItem },
        index: number,
      ) => boolean,
    ) => void;
  };
};

// `musickit-typescript` declares PlayerRepeatMode as a bare `0 | 1 | 2` type
// alias, explicitly noting it "is not exposed via the MusicKit namespace" —
// but the real v3 script does expose it as a runtime object. Mirrored here
// rather than fighting the stale types for a value that does exist.
export const RepeatMode = { none: 0, one: 1, all: 2 } as const;

// MusicKit only knows about Apple's catalog ids, but favoriting is a
// Streamsense concept keyed by our own song id. Whichever screen starts
// playback is the one that knows both, so it hands over this metadata to be
// cached here, keyed by catalog id, for as long as that song is playing.
interface SongMeta {
  songId: string;
  isFavorite: boolean;
}

interface MusicKitContextValue {
  isAvailable: boolean;
  isAuthorized: boolean;
  isPlaying: boolean;
  nowPlayingCatalogId: string | null;
  nowPlayingTitle: string | null;
  nowPlayingArtistName: string | null;
  nowPlayingArtworkURL: string | null;
  // the catalog id of a song whose play/pause/resume call is in flight, so
  // the UI can show a distinct "loading" state instead of a static icon.
  pendingCatalogId: string | null;
  currentPlaybackTime: number;
  currentPlaybackDuration: number;
  repeatMode: number;
  queueItems: MusicKit.MediaItem[];
  queuePosition: number;
  nowPlayingSongId: string | null;
  isNowPlayingFavorite: boolean;
  error: string | null;
  playSong: (catalogId: string, songMeta?: SongMeta) => Promise<void>;
  togglePlayPause: () => Promise<void>;
  seekToTime: (time: number) => Promise<void>;
  queueSong: (
    catalogId: string,
    position: 'next' | 'later',
    songMeta?: SongMeta,
  ) => Promise<void>;
  // resolves to whether the songs were queued.
  queueSongs: (
    songs: { catalogId: string; songMeta?: SongMeta }[],
  ) => Promise<boolean>;
  skipToNext: () => Promise<void>;
  skipToPrevious: () => Promise<void>;
  cycleRepeatMode: () => void;
  removeFromQueue: (index: number) => void;
  toggleFavorite: () => Promise<void>;
}

const noop = async () => undefined;

const defaultValue: MusicKitContextValue = {
  isAvailable: false,
  isAuthorized: false,
  isPlaying: false,
  nowPlayingCatalogId: null,
  nowPlayingTitle: null,
  nowPlayingArtistName: null,
  nowPlayingArtworkURL: null,
  pendingCatalogId: null,
  currentPlaybackTime: 0,
  currentPlaybackDuration: 0,
  repeatMode: RepeatMode.none,
  queueItems: [],
  queuePosition: 0,
  nowPlayingSongId: null,
  isNowPlayingFavorite: false,
  error: null,
  playSong: noop,
  togglePlayPause: noop,
  seekToTime: noop,
  queueSong: noop,
  queueSongs: async () => false,
  skipToNext: noop,
  skipToPrevious: noop,
  cycleRepeatMode: () => undefined,
  removeFromQueue: () => undefined,
  toggleFavorite: noop,
};

const MusicKitContext = createContext<MusicKitContextValue>(defaultValue);

export const useMusicKit = () => useContext(MusicKitContext);

export function MusicKitProvider({ children }: PropsWithChildren<unknown>) {
  // MusicKit JS is a browser-only SDK; the native RN app keeps using the
  // plain "open in Apple Music" link instead.
  if (!isWeb()) {
    return <>{children}</>;
  }

  return <WebMusicKitProvider>{children}</WebMusicKitProvider>;
}

function WebMusicKitProvider({ children }: PropsWithChildren<unknown>) {
  const [scriptLoaded, scriptError] = useScript(MUSICKIT_JS_SRC);
  const musicRef = useRef<MusicKit.MusicKitInstance | null>(null);

  const [isAvailable, setIsAvailable] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [nowPlayingCatalogId, setNowPlayingCatalogId] = useState<
    string | null
  >(null);
  const [nowPlayingTitle, setNowPlayingTitle] = useState<string | null>(null);
  const [nowPlayingArtistName, setNowPlayingArtistName] = useState<
    string | null
  >(null);
  const [nowPlayingArtworkURL, setNowPlayingArtworkURL] = useState<
    string | null
  >(null);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0);
  const [currentPlaybackDuration, setCurrentPlaybackDuration] = useState(0);
  const [repeatMode, setRepeatMode] = useState<number>(RepeatMode.none);
  const [queueItems, setQueueItems] = useState<MusicKit.MediaItem[]>([]);
  const [queuePosition, setQueuePosition] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [pendingCatalogId, setPendingCatalogId] = useState<string | null>(
    null,
  );
  const [catalogSongMeta, setCatalogSongMeta] = useState<
    Record<string, SongMeta>
  >({});

  // MusicKit JS's documented events (playbackStateDidChange,
  // nowPlayingItemDidChange, playbackTimeDidChange) didn't reliably fire in
  // practice against the real v3 script, so instead of depending on them,
  // this reads state directly off the instance: once right after any action
  // resolves (play/pause/seek), and continuously via polling below.
  const syncPlaybackState = (music: MusicKit.MusicKitInstance) => {
    const runtime = music as MKInstanceRuntime;
    const nowPlayingItem = runtime.nowPlayingItem;
    setNowPlayingCatalogId(nowPlayingItem?.id ?? null);
    setNowPlayingTitle(nowPlayingItem?.title ?? null);
    setNowPlayingArtistName(nowPlayingItem?.artistName ?? null);
    setNowPlayingArtworkURL(nowPlayingItem?.artworkURL ?? null);
    setIsPlaying(music.playbackState === MusicKit.PlaybackStates.playing);
    setCurrentPlaybackTime(runtime.currentPlaybackTime ?? 0);
    setCurrentPlaybackDuration(runtime.currentPlaybackDuration ?? 0);
    setRepeatMode(runtime.repeatMode ?? RepeatMode.none);
    setQueueItems(runtime.queue?.items ?? []);
    setQueuePosition(runtime.queue?.position ?? 0);
  };

  useEffect(() => {
    if (!isAvailable) {
      return;
    }
    const music = musicRef.current;
    if (!music) {
      return;
    }

    const intervalId = window.setInterval(() => {
      syncPlaybackState(music);
    }, 500);

    return () => window.clearInterval(intervalId);
  }, [isAvailable]);

  useEffect(() => {
    if (!scriptLoaded) {
      return;
    }
    if (scriptError) {
      setError('Could not load Apple Music.');
      return;
    }

    let cancelled = false;
    let configureStarted = false;
    let pollId: number | undefined;

    const configure = async () => {
      if (configureStarted) {
        return;
      }
      configureStarted = true;

      try {
        const tokenResponse = await fetchFromServer('/auth/token/apple');
        const developerToken = await tokenResponse.text();

        if (cancelled) {
          return;
        }

        // MusicKit JS v1 returns the instance synchronously; v3 returns a
        // Promise. Awaiting works correctly either way.
        const music = await MusicKit.configure({
          developerToken,
          app: { name: 'Streamsense', build: '1.0' },
        });

        if (cancelled) {
          return;
        }

        musicRef.current = music;
        setIsAuthorized(music.isAuthorized);
        syncPlaybackState(music);

        setIsAvailable(true);
      } catch (e) {
        logger.error('Failed to configure MusicKit', { error: e });
        setError('Could not connect to Apple Music.');
      }
    };

    if (typeof MusicKit !== 'undefined') {
      configure();
    } else {
      document.addEventListener('musickitloaded', configure, { once: true });
      // `musickitloaded` is dispatched via a `setTimeout` inside MusicKit JS
      // itself, which races unpredictably against our own effect timing; if
      // it never fires, fall back to polling for the global directly rather
      // than hanging forever.
      pollId = window.setInterval(() => {
        if (typeof MusicKit !== 'undefined') {
          window.clearInterval(pollId);
          configure();
        }
      }, 300);

      window.setTimeout(() => window.clearInterval(pollId), 15000);
    }

    return () => {
      cancelled = true;
      document.removeEventListener('musickitloaded', configure);
      window.clearInterval(pollId);
    };
  }, [scriptLoaded, scriptError]);

  const playSong = async (catalogId: string, songMeta?: SongMeta) => {
    const music = musicRef.current;
    if (!music) {
      setError('Apple Music is still loading. Please try again in a moment.');
      return;
    }

    if (songMeta) {
      setCatalogSongMeta((current) => ({
        ...current,
        [catalogId]: songMeta,
      }));
    }

    setPendingCatalogId(catalogId);
    try {
      if (!music.isAuthorized) {
        await music.authorize();
        setIsAuthorized(music.isAuthorized);
      }

      const alreadyQueued =
        (music as MKInstanceRuntime).nowPlayingItem?.id === catalogId;
      const needsRestart =
        music.playbackState === MusicKit.PlaybackStates.ended ||
        music.playbackState === MusicKit.PlaybackStates.stopped;

      if (!alreadyQueued || needsRestart) {
        // `items` is marked required in musickit-typescript's SetQueueOptions,
        // but Apple's actual API accepts `{ song: id }` on its own.
        await music.setQueue({ song: catalogId } as MusicKit.SetQueueOptions);
      }

      await music.play();
      syncPlaybackState(music);
      setError(null);
    } catch (e) {
      logger.error('Failed to play song via MusicKit', { error: e });
      setError(
        'Could not play this song. Do you have an active Apple Music subscription?',
      );
    } finally {
      setPendingCatalogId((current) => (current === catalogId ? null : current));
    }
  };

  const togglePlayPause = async () => {
    const music = musicRef.current;
    if (!music) {
      return;
    }

    if (music.playbackState === MusicKit.PlaybackStates.playing) {
      music.pause();
      syncPlaybackState(music);
      return;
    }

    const catalogId = (music as MKInstanceRuntime).nowPlayingItem?.id ?? null;

    setPendingCatalogId(catalogId);
    try {
      if (
        catalogId &&
        (music.playbackState === MusicKit.PlaybackStates.ended ||
          music.playbackState === MusicKit.PlaybackStates.stopped)
      ) {
        await music.setQueue({ song: catalogId } as MusicKit.SetQueueOptions);
      }

      await music.play();
      syncPlaybackState(music);
      setError(null);
    } catch (e) {
      logger.error('Failed to resume playback via MusicKit', { error: e });
      setError('Could not resume playback.');
    } finally {
      setPendingCatalogId((current) => (current === catalogId ? null : current));
    }
  };

  const seekToTime = async (time: number) => {
    const music = musicRef.current;
    if (!music) {
      return;
    }

    try {
      await music.seekToTime(time);
      syncPlaybackState(music);
    } catch (e) {
      logger.error('Failed to seek via MusicKit', { error: e });
    }
  };

  const queueSong = async (
    catalogId: string,
    position: 'next' | 'later',
    songMeta?: SongMeta,
  ) => {
    const music = musicRef.current;
    if (!music) {
      setError('Apple Music is still loading. Please try again in a moment.');
      return;
    }

    if (songMeta) {
      setCatalogSongMeta((current) => ({
        ...current,
        [catalogId]: songMeta,
      }));
    }

    try {
      if (!music.isAuthorized) {
        await music.authorize();
        setIsAuthorized(music.isAuthorized);
      }

      // `items` is marked required in musickit-typescript's SetQueueOptions,
      // but Apple's actual API accepts `{ song: id }` on its own.
      const options = { song: catalogId } as MusicKit.SetQueueOptions;
      if (position === 'next') {
        await music.playNext(options);
      } else {
        await music.playLater(options);
      }
      syncPlaybackState(music);
      setError(null);
    } catch (e) {
      logger.error('Failed to queue song via MusicKit', { error: e });
      setError('Could not add this song to the queue.');
    }
  };

  // adds songs to the end of the queue, in order. when nothing is playing
  // there's no queue to add to, so this starts playing them instead.
  const queueSongs = async (
    songs: { catalogId: string; songMeta?: SongMeta }[],
  ) => {
    const music = musicRef.current;
    if (!music) {
      setError('Apple Music is still loading. Please try again in a moment.');
      return false;
    }
    if (songs.length === 0) {
      return false;
    }

    setCatalogSongMeta((current) => {
      const next = { ...current };
      for (const { catalogId, songMeta } of songs) {
        if (songMeta) {
          next[catalogId] = songMeta;
        }
      }
      return next;
    });

    try {
      if (!music.isAuthorized) {
        await music.authorize();
        setIsAuthorized(music.isAuthorized);
      }

      // `items` is marked required in musickit-typescript's SetQueueOptions,
      // but Apple's actual API accepts `{ songs: ids }` on its own, queueing
      // them in the order given.
      const options = {
        songs: songs.map((song) => song.catalogId),
      } as MusicKit.SetQueueOptions;
      const runtime = music as MKInstanceRuntime;
      const nothingPlaying =
        runtime.nowPlayingItem == null ||
        music.playbackState === MusicKit.PlaybackStates.ended ||
        music.playbackState === MusicKit.PlaybackStates.stopped;

      if (nothingPlaying) {
        await music.setQueue(options);
        await music.play();
      } else {
        await music.playLater(options);
      }
      syncPlaybackState(music);
      setError(null);
      return true;
    } catch (e) {
      logger.error('Failed to queue songs via MusicKit', { error: e });
      setError('Could not add these songs to the queue.');
      return false;
    }
  };

  const skipToNext = async () => {
    const music = musicRef.current;
    if (!music) {
      return;
    }

    try {
      await music.skipToNextItem();
      syncPlaybackState(music);
    } catch (e) {
      logger.error('Failed to skip to next item via MusicKit', { error: e });
    }
  };

  const skipToPrevious = async () => {
    const music = musicRef.current;
    if (!music) {
      return;
    }

    try {
      await music.skipToPreviousItem();
      syncPlaybackState(music);
    } catch (e) {
      logger.error(
        'Failed to skip to previous item via MusicKit',
        { error: e },
      );
    }
  };

  const cycleRepeatMode = () => {
    const music = musicRef.current;
    if (!music) {
      return;
    }

    const runtime = music as MKInstanceRuntime;
    const current = runtime.repeatMode ?? RepeatMode.none;
    const next =
      current === RepeatMode.none
        ? RepeatMode.all
        : current === RepeatMode.all
          ? RepeatMode.one
          : RepeatMode.none;

    runtime.repeatMode = next;
    syncPlaybackState(music);
  };

  const removeFromQueue = (index: number) => {
    const music = musicRef.current;
    if (!music) {
      return;
    }

    try {
      (music as MKInstanceRuntime).queue?.removeQueueItems(
        (_queueItem, itemIndex) => itemIndex === index,
      );
      syncPlaybackState(music);
    } catch (e) {
      logger.error('Failed to remove item from queue via MusicKit', {
        error: e,
      });
    }
  };

  const nowPlayingMeta = nowPlayingCatalogId
    ? catalogSongMeta[nowPlayingCatalogId]
    : undefined;
  const nowPlayingSongId = nowPlayingMeta?.songId ?? null;
  const isNowPlayingFavorite = nowPlayingMeta?.isFavorite ?? false;

  const toggleFavorite = async () => {
    if (!nowPlayingCatalogId || !nowPlayingMeta) {
      return;
    }

    const nextIsFavorite = !nowPlayingMeta.isFavorite;
    try {
      await fetchFromServer(`/favorite/${nowPlayingMeta.songId}`, {
        method: 'POST',
        body: JSON.stringify({ favorite: nextIsFavorite }),
      });
      setCatalogSongMeta((current) => ({
        ...current,
        [nowPlayingCatalogId]: {
          ...nowPlayingMeta,
          isFavorite: nextIsFavorite,
        },
      }));
    } catch (e) {
      logger.error('Failed to toggle favorite', { error: e });
    }
  };

  return (
    <MusicKitContext.Provider
      value={{
        isAvailable,
        isAuthorized,
        isPlaying,
        nowPlayingCatalogId,
        nowPlayingTitle,
        nowPlayingArtistName,
        nowPlayingArtworkURL,
        pendingCatalogId,
        currentPlaybackTime,
        currentPlaybackDuration,
        repeatMode,
        queueItems,
        queuePosition,
        nowPlayingSongId,
        isNowPlayingFavorite,
        error,
        playSong,
        togglePlayPause,
        seekToTime,
        queueSong,
        queueSongs,
        skipToNext,
        skipToPrevious,
        cycleRepeatMode,
        removeFromQueue,
        toggleFavorite,
      }}
    >
      {children}
    </MusicKitContext.Provider>
  );
}
